import {AppAccessMobileApp, AppAccessMode, AppAccessPackage, AppAccessPurpose, AppAccessState, AppAccessTarget} from "@ots-frontend/ots-data";
import type {StatusTone} from "../components/StatusPill";
import {otsMobileApps} from "../support/ots-mobile-apps";

export type AppAccessAppOption = {
  id: AppAccessMobileApp;
  label: string;
  description: string;
  logo: string;
};

const mobileAppById = Object.fromEntries(otsMobileApps.map((app) => [app.id, app]));

export const appAccessApps: AppAccessAppOption[] = [
  {id: "WATER_GO", label: "WaterGo", description: "Water hauling", logo: mobileAppById["water-go"].logo},
  {id: "FIELD_GO", label: "FieldGo", description: "Field operations", logo: mobileAppById["field-go"].logo},
  {id: "OIL_GO", label: "OilGo", description: "Oil hauling", logo: mobileAppById["oil-go"].logo},
  {id: "DISPATCH_GO", label: "DispatchGo", description: "Dispatch operations", logo: mobileAppById["dispatch-go"].logo},
];

export const appAccessInputClass = "min-h-10 w-full rounded-lg border border-[var(--ots-outline)] bg-[var(--ots-surface)] px-3 py-2 text-sm text-[var(--ots-text)] focus:border-[var(--ots-primary)] focus:outline-none";
export const appAccessCardClass = "rounded-xl border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] p-4 shadow-sm";

export const getAppAccessAppLabel = (app: AppAccessMobileApp): string =>
  appAccessApps.find((option) => option.id === app)?.label ?? app;

export const getAppAccessApp = (app: AppAccessMobileApp): AppAccessAppOption | undefined =>
  appAccessApps.find((option) => option.id === app);

export const getAppAccessPurposeLabel = (purpose: AppAccessPurpose): string => ({
  INSTALL: "Install only",
  CONFIGURE: "Configure account",
  INSTALL_AND_CONFIGURE: "Install and configure",
})[purpose];

export const getAppAccessIdentityLabel = (target: AppAccessTarget): string | undefined =>
  target.appIdentityDisplayName ?? target.username ?? target.appIdentityId;

export const getAppAccessIdentitySummary = (targets: AppAccessTarget[]): string => {
  const identities = new Map<string, string>();
  targets.forEach((target) => {
    const label = getAppAccessIdentityLabel(target);
    const key = target.appIdentityId ?? target.username;
    if (label && key) identities.set(key, label);
  });

  const labels = [...identities.values()];
  if (labels.length === 0) return "No App Identity";
  if (labels.length === 1) return labels[0];
  return `${labels.length} App Identities · ${labels.join(", ")}`;
};

export type AppAccessDeliveryStatus = {
  key: AppAccessState | "PARTIAL" | "UNAVAILABLE";
  label: string;
  tone: StatusTone;
};

const targetStateStatus: Record<AppAccessState, AppAccessDeliveryStatus> = {
  ACTIVE: {key: "ACTIVE", label: "Ready to send", tone: "success"},
  EXPIRED: {key: "EXPIRED", label: "Expired", tone: "warning"},
  EXHAUSTED: {key: "EXHAUSTED", label: "Already used", tone: "neutral"},
  REVOKED: {key: "REVOKED", label: "Revoked", tone: "danger"},
};

export const getEffectiveAppAccessTargetState = (
  target: AppAccessTarget,
  nowDttm = Date.now(),
): AppAccessState => target.state === "ACTIVE"
  && typeof target.expiresDttm === "number"
  && target.expiresDttm <= nowDttm
    ? "EXPIRED"
    : target.state;

export const getAppAccessTargetDeliveryStatus = (
  target: AppAccessTarget,
  _mode: AppAccessMode,
  nowDttm = Date.now(),
): AppAccessDeliveryStatus => {
  const effectiveState = getEffectiveAppAccessTargetState(target, nowDttm);
  if (effectiveState !== "ACTIVE") return targetStateStatus[effectiveState];
  return targetStateStatus.ACTIVE;
};

export const canDeliverAppAccessTarget = (
  target: AppAccessTarget,
  mode: AppAccessMode,
  nowDttm = Date.now(),
): boolean => getAppAccessTargetDeliveryStatus(target, mode, nowDttm).key === "ACTIVE";

export const getAppAccessPackageDeliveryStatus = (
  appAccessPackage: AppAccessPackage,
  nowDttm = Date.now(),
): AppAccessDeliveryStatus => {
  if (appAccessPackage.revokedDttm) return targetStateStatus.REVOKED;
  if (appAccessPackage.targets.length === 0) {
    return {key: "UNAVAILABLE", label: "Unavailable", tone: "neutral"};
  }

  const statuses = appAccessPackage.targets.map((target) =>
    getAppAccessTargetDeliveryStatus(target, appAccessPackage.mode, nowDttm));
  if (statuses.every((status) => status.key === "ACTIVE")) return targetStateStatus.ACTIVE;
  if (statuses.some((status) => status.key === "ACTIVE")) {
    return {key: "PARTIAL", label: "Partially available", tone: "warning"};
  }

  return statuses.find((status) => status.key === "EXPIRED")
    ?? statuses.find((status) => status.key === "REVOKED")
    ?? statuses.find((status) => status.key === "EXHAUSTED")
    ?? {key: "UNAVAILABLE", label: "Unavailable", tone: "neutral"};
};

export const canDeliverAppAccessPackage = (
  appAccessPackage: AppAccessPackage,
  nowDttm = Date.now(),
): boolean => appAccessPackage.targets.length > 0
  && !appAccessPackage.revokedDttm
  && appAccessPackage.targets.every((target) =>
    canDeliverAppAccessTarget(target, appAccessPackage.mode, nowDttm));
